import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { jwtDecode } from "jwt-decode";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const logoutTimerRef = useRef(null);
  const inactivityTimerRef = useRef(null);

  // Inactivity timeout in milliseconds (30 minutes)
  const INACTIVITY_TIMEOUT = 30 * 60 * 1000;

  // Function to clear timers
  const clearTimers = () => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  };

  // Function to reset inactivity timer
  const resetInactivityTimer = () => {
    // Only clear inactivity timer, not the logout timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (user && token) {
      inactivityTimerRef.current = setTimeout(() => {
        console.log("Auto-logout due to inactivity");
        logout();
      }, INACTIVITY_TIMEOUT);
    }
  };

  // Function to set logout timer based on token expiry
  const setLogoutTimer = (token) => {
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      const timeUntilExpiry = (decoded.exp - currentTime) * 1000;

      // Clear any existing timer
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
      }

      if (timeUntilExpiry > 0) {
        console.log(
          `Setting logout timer for ${Math.round(
            timeUntilExpiry / 1000 / 60
          )} minutes from now`
        );
        logoutTimerRef.current = setTimeout(() => {
          console.log("Auto-logout due to token expiry");
          logout();
        }, timeUntilExpiry);
      } else {
        // Token already expired
        console.log("Token already expired, logging out immediately");
        logout();
      }
    } catch (error) {
      console.error("Error decoding token:", error);
      logout();
    }
  };

  // Login function
  const login = (userData, tokenData) => {
    localStorage.setItem("token", tokenData);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
    setToken(tokenData);
    setLogoutTimer(tokenData);
    resetInactivityTimer();
  };

  // Logout function
  const logout = () => {
    clearTimers();
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    setToken(null);
    // Redirect to login
    window.location.href = "/login";
  };

  // Check for existing session on mount
  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");

    if (storedToken && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        setToken(storedToken);
        setLogoutTimer(storedToken);
        resetInactivityTimer();
      } catch (error) {
        console.error("Error parsing stored user data:", error);
        logout();
      }
    }
    setLoading(false);
  }, []);

  // Set up activity listeners for inactivity detection
  useEffect(() => {
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
    ];

    const handleActivity = () => {
      resetInactivityTimer();
    };

    events.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [user, token]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, []);

  const value = {
    user,
    token,
    loading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
